import React, { type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppHeader from './AppHeader';
import Button from './ui/Button';
import OrgSwitcher from './ui/OrgSwitcher';
import NotificationBell from './NotificationBell';
import TimesheetClockButton from './TimesheetClockButton';
import {
    ArrowLeft, Home, Clock, LogOut,
    Wrench, FlaskConical, Briefcase, Trees, Leaf, Users, UserCog,
    Droplets, Activity, CheckCircle, DollarSign, CreditCard, Sprout, ClipboardList, Landmark, ShoppingCart, Headphones
} from 'lucide-react';

const SECTION_MAP: Record<string, { label: string; icon: ReactNode; path: string }> = {
    '/equipment':              { label: 'Equipment',     icon: <Wrench className="h-4 w-4" />,       path: '/equipment' },
    '/spray':                  { label: 'Spray',         icon: <FlaskConical className="h-4 w-4" />,  path: '/spray' },
    '/jobs':                   { label: 'Jobs',          icon: <Briefcase className="h-4 w-4" />,     path: '/jobs' },
    '/nursery':                { label: 'Nursery',       icon: <Sprout className="h-4 w-4" />,        path: '/nursery' },
    '/purchase-orders':        { label: 'Purchase Orders', icon: <ShoppingCart className="h-4 w-4" />, path: '/purchase-orders' },
    '/accounts':               { label: 'Accounts',      icon: <Landmark className="h-4 w-4" />,      path: '/accounts' },
    '/sales':                  { label: 'Sales',         icon: <Headphones className="h-4 w-4" />,    path: '/sales' },
    '/orchard':                { label: 'Farms',         icon: <Trees className="h-4 w-4" />,         path: '/orchard' },
    // The cultivar admin sub-pages (growing regions, attribute definitions,
    // crop types, programs) are reached from the admin hub, so their section
    // button returns there rather than to the registry. Longest prefix wins,
    // so this matches before '/cultivar'.
    '/cultivar/admin':         { label: 'Cultivar Admin', icon: <Leaf className="h-4 w-4" />,         path: '/cultivar/admin' },
    '/cultivar':               { label: 'Cultivar',      icon: <Leaf className="h-4 w-4" />,          path: '/cultivar' },
    // These admin sub-pages are navigated to from HR, so the section button
    // should return to HR rather than the org admin page.
    '/admin/work-schedule':    { label: 'HR',            icon: <UserCog className="h-4 w-4" />,       path: '/hr' },
    '/admin/public-holidays':  { label: 'HR',            icon: <UserCog className="h-4 w-4" />,       path: '/hr' },
    // The admin certifications register is reached from HR, so its section
    // button returns to HR; the self-service register is its own section.
    '/certifications/admin':   { label: 'HR',            icon: <UserCog className="h-4 w-4" />,       path: '/hr' },
    '/certifications':         { label: 'Certifications', icon: <ClipboardList className="h-4 w-4" />, path: '/certifications' },
    '/admin':                  { label: 'Admin',         icon: <Users className="h-4 w-4" />,         path: '/admin' },
    '/hr':                     { label: 'HR',            icon: <UserCog className="h-4 w-4" />,       path: '/hr' },
    '/irrigation':             { label: 'Irrigation',    icon: <Droplets className="h-4 w-4" />,      path: '/irrigation' },
    '/soil-sensors':           { label: 'Sensors',       icon: <Activity className="h-4 w-4" />,      path: '/soil-sensors' },
    '/timesheet':              { label: 'Timesheet',     icon: <Clock className="h-4 w-4" />,         path: '/timesheet' },
    '/approvals':              { label: 'Approvals',     icon: <CheckCircle className="h-4 w-4" />,   path: '/approvals' },
    // Payroll is opened from the HR & Payroll page, so it returns there.
    '/payroll':                { label: 'HR & Payroll',  icon: <DollarSign className="h-4 w-4" />,    path: '/hr' },
    '/subscriptions':          { label: 'Subscriptions', icon: <CreditCard className="h-4 w-4" />,    path: '/subscriptions' },
};

// Sorted longest-first so /soil-sensors matches before /soil (if it existed)
const SECTION_PREFIXES = Object.keys(SECTION_MAP).sort((a, b) => b.length - a.length);

function useCurrentSection() {
    const { pathname } = useLocation();
    for (const prefix of SECTION_PREFIXES) {
        if (pathname === prefix || pathname.startsWith(prefix + '/')) {
            return {
                section: SECTION_MAP[prefix],
                // The root is where the section button goes, which for a page
                // filed under another section (payroll under HR) is not its own prefix.
                isAtRoot: pathname === SECTION_MAP[prefix].path,
            };
        }
    }
    return { section: null, isAtRoot: false };
}

interface PageHeaderProps {
    title: string;
    subtitle?: string;
    icon?: ReactNode;
    showBackButton?: boolean;
    /** Where Back goes, for a page whose "back" is not the previous history
     *  entry (a deep link, or a view switched in place). Default: navigate(-1). */
    onBack?: () => void;
    showDashboardButton?: boolean;
    showHistoryButton?: boolean;
    onHistoryClick?: () => void;
    showUserInfo?: boolean;
    showLogoutButton?: boolean;
    showSystemStatus?: boolean;
    actionButtons?: ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({
    title,
    subtitle,
    icon,
    showBackButton = true,
    onBack,
    showDashboardButton = true,
    showHistoryButton = false,
    onHistoryClick,
    showUserInfo = false,
    showLogoutButton = false,
    showSystemStatus = false,
    actionButtons
}) => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { section, isAtRoot } = useCurrentSection();

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const hasActionRow = actionButtons || (showHistoryButton && onHistoryClick) || showSystemStatus;

    // Whether to show the section button (only when in a section and not already at its root)
    const showSectionButton = showDashboardButton && section && !isAtRoot;

    // Shared right-side control cluster (clock, notifications, org switcher,
    // section/dashboard nav, logout). Reused by the two-row layout and the
    // merged single-row dashboard layout so behaviour stays identical.
    const navControls = (
        <>
            <TimesheetClockButton />
            <NotificationBell />
            <OrgSwitcher />

            {/* Section button — on mobile: shown only on sub-pages (replaces dashboard).
                On desktop: always shown alongside dashboard. */}
            {showSectionButton && (
                <Button
                    onClick={() => navigate(section.path)}
                    variant="outline"
                    size="sm"
                    className="sm:flex"
                    aria-label={section.label}
                >
                    {section.icon}
                    <span className="hidden sm:inline">{section.label}</span>
                </Button>
            )}

            {/* Dashboard button — on mobile: hidden when section button is showing.
                On desktop: always shown. */}
            {showDashboardButton && (
                <Button
                    onClick={() => navigate('/dashboard')}
                    variant="outline"
                    size="sm"
                    className={showSectionButton ? 'hidden sm:flex' : ''}
                    aria-label="Dashboard"
                >
                    <Home className="h-4 w-4" />
                    <span className="hidden sm:inline">Dashboard</span>
                </Button>
            )}

            {showLogoutButton && (
                <Button
                    onClick={handleLogout}
                    variant="outline"
                    size="sm"
                    aria-label="Logout"
                >
                    <LogOut className="h-4 w-4" />
                    <span className="hidden sm:inline">Logout</span>
                </Button>
            )}
        </>
    );

    return (
        <AppHeader>
            {/* Mobile single-row layout for dashboard-style headers (showUserInfo).
                Replaces Row 1 + Row 2 on small screens with: [icon] [username] [org] [controls] */}
            {showUserInfo && user && (
                <div className="flex sm:hidden items-center gap-2 py-2">
                    {icon && (
                        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center flex-shrink-0">
                            {typeof icon === 'string' ? (
                                <span className="text-base">{icon}</span>
                            ) : (
                                icon
                            )}
                        </div>
                    )}
                    <span className="text-sm font-semibold text-neutral-900 truncate">{user.username}</span>
                    <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-md truncate">
                        {user.organisation_name || 'No Organisation'}
                    </span>
                    <div className="flex items-center gap-1 ml-auto">
                        <TimesheetClockButton />
                        <NotificationBell />
                        <OrgSwitcher />
                        {showLogoutButton && (
                            <Button
                                onClick={handleLogout}
                                variant="outline"
                                size="sm"
                                aria-label="Logout"
                            >
                                <LogOut className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Mobile two-row layout for standard (non-dashboard) pages.
                Dashboard-style pages use the compact single-row block above. */}
            {!(showUserInfo && user) && (
                <div className="sm:hidden">
                    {/* Row 1: icon + title + subtitle */}
                    <div className="flex items-center gap-3 pt-3 pb-2">
                        {icon && (
                            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center flex-shrink-0">
                                {typeof icon === 'string' ? (
                                    <span className="text-xl">{icon}</span>
                                ) : (
                                    icon
                                )}
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <h1 className="text-xl font-bold tracking-tight text-neutral-900 leading-tight break-words">{title}</h1>
                            {subtitle && (
                                <p className="text-sm text-neutral-500 mt-0.5 break-words">{subtitle}</p>
                            )}
                        </div>
                    </div>

                    {/* Row 2: back button (left) + nav controls (right) */}
                    <div className="flex items-center justify-between py-1.5 border-t border-neutral-100">
                        <div className="flex items-center">
                            {showBackButton && (
                                <Button
                                    onClick={onBack ?? (() => navigate(-1))}
                                    variant="outline"
                                    size="sm"
                                    aria-label="Back"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {navControls}
                        </div>
                    </div>
                </div>
            )}

            {/* Desktop: one compact row for EVERY page —
                [back] [icon] [title + subtitle/welcome] <spacer> [controls].
                Mobile layouts are handled by the two blocks above.
                The row wraps: the title claims at least 16rem before the
                controls share its line. Without that, a page that mounts the
                header in a narrow column (max-w-2xl forms) squeezes the title
                down to one letter per line, since the controls cannot shrink. */}
            <div className="hidden sm:flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5">
                {showBackButton && (
                    <Button
                        onClick={onBack ?? (() => navigate(-1))}
                        variant="outline"
                        size="sm"
                        aria-label="Back"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <span>Back</span>
                    </Button>
                )}
                {icon && (
                    <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center flex-shrink-0">
                        {typeof icon === 'string' ? (
                            <span className="text-xl">{icon}</span>
                        ) : (
                            icon
                        )}
                    </div>
                )}
                <div className="min-w-0 flex-[1_1_16rem]">
                    <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-neutral-900 leading-tight break-words">{title}</h1>
                    {subtitle ? (
                        <p className="text-sm text-neutral-500 mt-0.5 break-words">{subtitle}</p>
                    ) : showUserInfo && user ? (
                        <div className="flex flex-wrap items-center gap-2 text-sm mt-0.5">
                            <p className="text-neutral-500">Welcome back, {user.username}</p>
                            <span className="text-neutral-300">•</span>
                            <span className="text-sm font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-md">
                                {user.organisation_name || 'No Organisation'}
                            </span>
                        </div>
                    ) : null}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
                    {navControls}
                </div>
            </div>

            {/* Row 3: Page-specific action buttons (conditional) */}
            {hasActionRow && (
                <div className="flex items-center gap-2 py-1.5 border-t border-neutral-100 flex-wrap">
                    {actionButtons}
                    {showHistoryButton && onHistoryClick && (
                        <Button
                            onClick={onHistoryClick}
                            variant="outline"
                            size="sm"
                        >
                            <Clock className="h-4 w-4" />
                            History
                        </Button>
                    )}
                    {showSystemStatus && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-100 rounded-lg">
                            <div className="h-2 w-2 bg-success-500 rounded-full animate-pulse"></div>
                            <span className="text-sm font-medium text-neutral-700">System Online</span>
                        </div>
                    )}
                </div>
            )}
        </AppHeader>
    );
};

export default PageHeader;
